import { Button, makeStyles, mergeClasses, Popover, PopoverSurface, PopoverTrigger, tokens } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { BaseSyntheticEvent, KeyboardEvent, PropsWithChildren, ReactNode } from 'react';
import { customPalette } from '../../theme';

export interface IIconButtonWithCalloutProps {
    buttonId?: string;
    label: string;
    ariaLabel?: string;
    icon: ReactNode;
    onButtonClick?: () => void;
    isDialogVisible?: boolean;
    toggleDialogVisible?: () => void;
    dialogClassName?: string;
    disabled?: boolean;
    onCalloutSubmit?: () => void;
}

const useStyles = makeStyles({
    surface: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `${tokens.strokeWidthThin} solid ${customPalette.neutralSecondaryAlt}`,
        boxShadow: '0px 1.2px 3.6px rgba(0, 0, 0, 0.1), 0px 6.4px 14.4px rgba(0, 0, 0, 0.13)',

        overflowX: 'hidden',
        maxWidth: '530px',
        maxHeight: '50vh',
        padding: 0,
        zIndex: 12,
        '& > div': {
            overflow: 'hidden',
        },
    },
});

/**
 * Button trigger + Fluent 9 Popover with trapFocus (replaces FocusTrapCallout).
 * F0.1 PopoverCallout is a poor fit here (controlled open, labeled secondary/subtle
 * chrome, Enter-to-submit) — this duplicates the Popover shell once, same as
 * FilterPicker.
 */
const IconButtonWithCallout: React.FC<PropsWithChildren<IIconButtonWithCalloutProps>> = ({
    buttonId,
    label,
    ariaLabel,
    icon,
    onButtonClick,
    children,
    dialogClassName,
    toggleDialogVisible: toggleIsDialogVisibleOverride,
    isDialogVisible: isDialogVisibleOverride,
    disabled,
    onCalloutSubmit,
}) => {
    const styles = useStyles();
    const [isDialogVisible, { toggle: toggleIsDialogVisible }] = useBoolean(false);

    const isVisible = isDialogVisibleOverride ?? isDialogVisible;

    const setVisible = (open: boolean) => {
        const currentlyVisible = isDialogVisibleOverride ?? isDialogVisible;
        if (open === currentlyVisible) {
            return;
        }
        if (toggleIsDialogVisibleOverride) {
            toggleIsDialogVisibleOverride();
        } else {
            toggleIsDialogVisible();
        }
    };

    const onButtonClickWrapper = () => {
        setVisible(true);
        onButtonClick?.();
    };

    const callSubmitHandlerIfEnter = (event: BaseSyntheticEvent) => {
        const keyboardEvent = event as KeyboardEvent;

        if (keyboardEvent.key === 'Enter' && onCalloutSubmit) {
            event.preventDefault();
            onCalloutSubmit();
        }
    };

    return (
        <Popover
            open={isVisible}
            onOpenChange={(_ev, data) => {
                if (data.open) {
                    onButtonClickWrapper();
                } else {
                    setVisible(false);
                }
            }}
            positioning={{ position: 'below', align: 'start', offset: { mainAxis: 10 }, withArrow: false }}
            trapFocus
        >
            <PopoverTrigger disableButtonEnhancement>
                <Button
                    id={buttonId}
                    icon={icon}
                    appearance='secondary'
                    aria-label={ariaLabel ?? label}
                    aria-expanded={isVisible}
                    aria-haspopup='dialog'
                    disabled={disabled}
                >
                    {label}
                </Button>
            </PopoverTrigger>
            <PopoverSurface
                className={mergeClasses(styles.surface, dialogClassName)}
                role='dialog'
                data-testid='icon-button-callout'
                aria-label={ariaLabel ?? label}
                onKeyDown={callSubmitHandlerIfEnter}
            >
                {children}
            </PopoverSurface>
        </Popover>
    );
};

export default IconButtonWithCallout;
